// libs
import React from 'react';
import PropTypes from 'prop-types';
// utils
import { formatNumber } from '@biw/intl';
import { getRoundingPrecision } from '@biw/contests-core/lib/dux/progress/utils';
// shared components
import AnimatedNumber from '../animated-number';
import Label from './label';
import Wrapper from './wrapper';

/*
 * GoalLabel
 *
 * GoalLabel is complex. Check out the documentation:
 * https://biworldwideg5.atlassian.net/wiki/display/GOAL/Internationalization+Standards
 *
 */

const getFormatStyle = ( labelType ) => {
    switch ( labelType ) {
        case 'percentage':
            return 'percent';
        case 'currency':
            return 'currency';
        default:
            return 'decimal';
    }
};

export default class GoalLabel extends React.Component {
    static propTypes = {
        amount: PropTypes.number,
        animated: PropTypes.bool,
        animatedOpts: PropTypes.object,
        className: PropTypes.string,
        currencyCode: PropTypes.string,
        labelPosition: PropTypes.string, // before, after -> only exist if labelType is 'other'
        labelText: PropTypes.string,
        labelType: PropTypes.string, // currency, percent, other, none
        locale: PropTypes.shape( {
            code: PropTypes.string // e.g. en_US
        } ),
        precision: PropTypes.number,
        rounding: PropTypes.string,
        strong: PropTypes.bool
    }

    static defaultProps = {
        labelText: '',
        locale: { code: 'en_US', intlCode: 'en-US' },
        animated: false,
        currencyCode: ''
    }

    formatNumber = ( amount ) => {
        const {
            currencyCode = '',
            labelType,
            precision,
            locale
        } = this.props;
        const formatOptions = {
            minimumFractionDigits: precision || 0,
            maximumFractionDigits: precision || 2,
            style: getFormatStyle( labelType )
        };

        if ( labelType === 'currency' ) {
            if ( currencyCode ) {
                formatOptions.currency = currencyCode.toUpperCase();
            } else {
                console.error( 'Missing currency code found for goal label with a labelType of `currency`' );
                formatOptions.style = 'decimal';
            }
        }

        return formatNumber( locale, formatOptions, amount );
    }

    getDisplayAmount = () => {
        const { amount: rawAmount, labelType, precision, rounding, } = this.props;
        const amount = rawAmount !== 'number' ? parseFloat( rawAmount ) : rawAmount;
        if ( isNaN( amount ) ) {
            return null;
        }

        const displayAmount = getRoundingPrecision( rounding, amount, precision );

        // Intl.formatNumber expects percentage numbers in 0-1 for 0%-100% format
        return labelType === 'percentage' ? displayAmount / 100 : displayAmount;
    }

    render() {
        const {
            animated,
            animatedOpts,
            labelPosition,
            labelText,
            className,
            strong
        } = this.props;
        const showLabelBefore = labelPosition === 'before';
        const displayAmount = this.getDisplayAmount();

        return (
            <Wrapper strong={strong} className={className}>
                { showLabelBefore && <Label value={labelText} />}
                { displayAmount !== null &&
                    <span className="goal-label-amount">
                        { animated ?
                            <AnimatedNumber value={ displayAmount } formatter={this.formatNumber} { ...animatedOpts } />
                        :
                            this.formatNumber( displayAmount )
                        }
                    </span>
                }
                { !showLabelBefore && <Label value={labelText} />}
            </Wrapper>
        );
    }
}
